/**
 * Утилиты для управления вибрацией устройства в Telegram Web App
 */

export interface VibrationPattern {
  pattern: number | number[]
  telegramStyle?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
  telegramType?: 'error' | 'success' | 'warning'
  description: string
}

// Предустановленные паттерны вибрации
export const VIBRATION_PATTERNS = {
  // Короткая вибрация для обычных кнопок
  button: {
    pattern: 50 as const,
    telegramStyle: 'light' as const,
    description: 'Короткая вибрация для кнопок'
  },
  
  // Средняя вибрация для важных действий
  success: {
    pattern: [100, 50, 100] as const,
    telegramType: 'success' as const,
    description: 'Двойная вибрация для успешных действий'
  },
  
  // Длинная вибрация для ошибок
  error: {
    pattern: 200 as const,
    telegramType: 'error' as const,
    description: 'Длинная вибрация для ошибок'
  },
  
  // Тройная вибрация для особо важных действий
  critical: {
    pattern: [50, 50, 50, 50, 50] as const,
    telegramStyle: 'heavy' as const,
    description: 'Тройная вибрация для критических действий'
  }
}

/**
 * Проверяет, является ли приложение Telegram Web App
 */
export const isTelegramWebApp = (): boolean => {
  return !!(window.Telegram?.WebApp)
}

/**
 * Проверяет, поддерживает ли устройство API вибрации
 */
export const isVibrationSupported = (): boolean => {
  if (isTelegramWebApp()) {
    return !!(window.Telegram?.WebApp?.HapticFeedback)
  }
  return 'vibrate' in navigator
}

/**
 * Вызывает вибрацию через Telegram HapticFeedback
 */
const vibrateTelegram = (pattern: any): boolean => {
  try {
    const haptic = window.Telegram?.WebApp?.HapticFeedback
    if (!haptic) return false

    // Используем Telegram-специфичные методы
    if ('telegramType' in pattern && pattern.telegramType) {
      haptic.notificationOccurred(pattern.telegramType)
      return true
    }
    
    if ('telegramStyle' in pattern && pattern.telegramStyle) {
      haptic.impactOccurred(pattern.telegramStyle)
      return true
    }

    // Fallback на селection changed для обычных кнопок
    haptic.selectionChanged()
    return true
  } catch (error) {
    console.error('Ошибка при вызове Telegram HapticFeedback:', error)
    return false
  }
}

/**
 * Вызывает вибрацию с заданным паттерном
 * @param pattern - Паттерн вибрации (число или массив чисел)
 * @returns Promise<boolean> - true если вибрация была вызвана, false если не поддерживается
 */
export const vibrate = async (pattern: number | number[]): Promise<boolean> => {
  console.log(`🔔 Standard vibrate called with pattern:`, pattern)
  
  if (!isVibrationSupported()) {
    console.warn('Vibration API не поддерживается на этом устройстве')
    return false
  }

  // Если это Telegram Web App, используем стандартную тактильную обратную связь
  if (isTelegramWebApp()) {
    try {
      const haptic = window.Telegram?.WebApp?.HapticFeedback
      if (haptic) {
        // Для простых паттернов используем light impact
        haptic.impactOccurred('light')
        console.log(`🔔 Telegram standard vibrate: success`)
        return true
      }
    } catch (error) {
      console.error('Ошибка при вызове Telegram вибрации:', error)
    }
    return false
  }

  // Fallback на стандартный Vibration API
  try {
    console.log(`🔔 Calling navigator.vibrate with:`, pattern)
    const result = navigator.vibrate(pattern)
    console.log(`🔔 Navigator.vibrate result:`, result)
    return result
  } catch (error) {
    console.error('Ошибка при вызове вибрации:', error)
    return false
  }
}

/**
 * Вызывает вибрацию по предустановленному паттерну
 * @param patternName - Название паттерна из VIBRATION_PATTERNS
 * @returns Promise<boolean>
 */
export const vibratePattern = async (patternName: keyof typeof VIBRATION_PATTERNS): Promise<boolean> => {
  const pattern = VIBRATION_PATTERNS[patternName]
  if (!pattern) {
    console.warn(`Паттерн вибрации "${patternName}" не найден`)
    return false
  }

  console.log(`🔔 Вибрация: ${patternName}`, {
    pattern: pattern.pattern,
    isTelegram: isTelegramWebApp(),
    isSupported: isVibrationSupported(),
    userAgent: navigator.userAgent,
    platform: navigator.platform
  })

  if (!isVibrationSupported()) {
    console.warn('Vibration API не поддерживается на этом устройстве')
    return false
  }

  // Если это Telegram Web App, используем специальные методы
  if (isTelegramWebApp()) {
    const result = vibrateTelegram(pattern)
    console.log(`🔔 Результат Telegram вибрации: ${result}`)
    return result
  }

  // Fallback на стандартный Vibration API
  const result = await vibrate(pattern.pattern as number | number[])
  console.log(`🔔 Результат стандартной вибрации: ${result}`)
  return result
}

/**
 * Останавливает текущую вибрацию
 * @returns boolean
 */
export const stopVibration = (): boolean => {
  // В Telegram Web App нет способа остановить тактильную обратную связь
  if (isTelegramWebApp()) {
    return true
  }

  if (!isVibrationSupported()) {
    return false
  }

  try {
    return navigator.vibrate(0)
  } catch (error) {
    console.error('Ошибка при остановке вибрации:', error)
    return false
  }
}

/**
 * Composable для использования вибрации в компонентах Vue
 */
export const useVibration = () => {
  return {
    vibrate,
    vibratePattern,
    stopVibration,
    isVibrationSupported,
    isTelegramWebApp,
    VIBRATION_PATTERNS
  }
}
